import React, {useEffect, useMemo, useState} from "react";
import {ImagesByStation, stationNames} from "../utils/images";

type Props = {
    imagesByStation: ImagesByStation;
    toMediaUrl: (path: string) => string;
    openLinksPage: (title: string, links: string[]) => void;
};

export const ImageCarousel: React.FC<Props> = ({imagesByStation, toMediaUrl, openLinksPage}) => {
    const stations = useMemo(() => stationNames(imagesByStation), [imagesByStation]);
    const stationsKey = stations.join("|");

    const [station, setStation] = useState<string>(stations[0] ?? "");
    useEffect(() => {
        setStation((prev) => (prev && stations.includes(prev) ? prev : stations[0] ?? ""));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stationsKey]);

    const images = useMemo(
        () => (station ? imagesByStation[station] ?? [] : []),
        [imagesByStation, station]
    );

    const [carouselIndex, setCarouselIndex] = useState<number>(0);
    const carouselKey = useMemo(() => images.join("|"), [images]);

    useEffect(() => {
        setCarouselIndex(0);
    }, [carouselKey]);

    const goPrev = () => {
        if (images.length === 0) {
            return;
        }
        setCarouselIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const goNext = () => {
        if (images.length === 0) {
            return;
        }
        setCarouselIndex((prev) => (prev + 1) % images.length);
    };

    const openImage = (p: string) => {
        window.open(toMediaUrl(p), "_blank", "noreferrer");
    };

    // Apre il visualizzatore HDF5 sul file .h5 corrispondente alla png corrente
    // (stesso nome, estensione diversa).
    const openHdf5Viewer = (p: string) => {
        const h5Path = p.replace(/\.png$/i, ".h5");
        const media = toMediaUrl(h5Path);
        const absolute = media.startsWith("/") ? `${window.location.origin}${media}` : media;
        const url = `https://wewair.netfarm.it/hdf5viewer/?url=${encodeURIComponent(absolute)}`;
        window.open(url, "_blank", "noreferrer");
    };

    return (
        <section className="carousel-section">
            {stations.length === 0 ? (
                <div className="empty-state">Nessuna immagine disponibile (lista_path_immagini vuota).</div>
            ) : (
                <div className="carousel">
                    <div className="station-tabs">
                        {stations.map((s) => (
                            <button
                                key={s}
                                className={`station-tab ${s === station ? "active" : ""}`}
                                onClick={() => setStation(s)}
                                aria-pressed={s === station}
                            >
                                {s} ({imagesByStation[s].length})
                            </button>
                        ))}
                    </div>
                    <div className="carousel-top">
                        <button className="carousel-btn" onClick={goPrev} aria-label="Immagine precedente">
                            ←
                        </button>
                        <div className="carousel-frame" onClick={() => openImage(images[carouselIndex])}>
                            <img
                                className="carousel-image"
                                src={toMediaUrl(images[carouselIndex])}
                                alt={`Immagine ${carouselIndex + 1} di ${images.length} - stazione ${station}`}
                                loading="lazy"
                            />
                        </div>
                        <button className="carousel-btn" onClick={goNext} aria-label="Immagine successiva">
                            →
                        </button>
                    </div>
                    <div className="carousel-meta">
                        <div className="mono">
                            {station} · {carouselIndex + 1}/{images.length}
                        </div>
                        <div className="carousel-actions">
                            <button
                                className="link-button"
                                onClick={() => openHdf5Viewer(images[carouselIndex])}
                                title="Apri il file HDF5 corrispondente nel visualizzatore"
                            >
                                Visualizza HDF5
                            </button>
                            <button
                                className="link-button"
                                onClick={() => openLinksPage(`Immagini ${station} (ultimo output)`, images)}
                            >
                                Apri lista
                            </button>
                        </div>
                    </div>
                    <div className="carousel-thumbs">
                        {images.map((p, idx) => (
                            <button
                                key={`${p}-${idx}`}
                                className={`thumb ${idx === carouselIndex ? "active" : ""}`}
                                onClick={() => setCarouselIndex(idx)}
                                title={p}
                                aria-label={`Vai a immagine ${idx + 1}`}
                            >
                                <img className="thumb-img" src={toMediaUrl(p)} alt="" loading="lazy" />
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};
